import { Box, Chip, Typography } from "@mui/material";
import React from "react";
import { getScoreHex } from "./scoreColors";

interface ScoreCircleProps {
  score: number;
  confidence?: number;
  size?: "small" | "medium" | "large";
  scoreScale?: "0-1" | "1-5";
  showConfidence?: boolean;
}

const ScoreCircle: React.FC<ScoreCircleProps> = ({
  score,
  confidence,
  size = "medium",
  scoreScale = "1-5",
  showConfidence = false,
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case "small":
        return { diameter: 48, radius: 20, strokeWidth: 4, fontSize: "0.75rem" };
      case "medium":
        return { diameter: 64, radius: 28, strokeWidth: 5, fontSize: "1rem" };
      case "large":
        return { diameter: 96, radius: 42, strokeWidth: 6, fontSize: "1.5rem" };
    }
  };

  const getScoreColor = (value: number): string => {
    if (scoreScale === "0-1") {
      if (value >= 0.8) return "#4caf50";
      if (value >= 0.5) return "#ffc107";
      return "#f44336";
    } else {
      return getScoreHex(value);
    }
  };

  const getDisplayScore = (): string => {
    if (scoreScale === "0-1") {
      return Math.round(score * 100).toString();
    } else {
      return score.toFixed(1);
    }
  };

  const getNormalizedScore = (): number => {
    if (scoreScale === "0-1") {
      return score;
    } else {
      return score / 5;
    }
  };

  const config = getSizeConfig();
  const circumference = 2 * Math.PI * config.radius;
  const normalizedScore = getNormalizedScore();
  const strokeDashoffset = circumference * (1 - normalizedScore);
  const color = getScoreColor(score);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <svg
          width={config.diameter}
          height={config.diameter}
          style={{ transform: "rotate(-90deg)" }}
        >
          <circle
            cx={config.diameter / 2}
            cy={config.diameter / 2}
            r={config.radius}
            fill="none"
            stroke="rgba(0, 0, 0, 0.1)"
            strokeWidth={config.strokeWidth}
          />
          <circle
            cx={config.diameter / 2}
            cy={config.diameter / 2}
            r={config.radius}
            fill="none"
            stroke={color}
            strokeWidth={config.strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: config.fontSize,
              color: color,
            }}
          >
            {getDisplayScore()}
          </Typography>
        </Box>
      </Box>
      {showConfidence && confidence !== undefined && (
        <Chip
          label={`${Math.round(confidence * 100)}%`}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 600,
          }}
        />
      )}
    </Box>
  );
};

export default ScoreCircle;
